import { createDraftSafeSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { GlobalState } from '@store'
import {
  CommissionPreview,
  CommissionSettlement,
  CreateSettlementPayload,
  DeleteSettlementPayload,
  DeleteSettlementResult,
  ExecutiveOption
} from '../types'

interface LoadPayload {
  period: string
  executiveId?: string
}

interface ExecutiveCommissionsState {
  executives: ExecutiveOption[]
  preview?: CommissionPreview
  settlements: CommissionSettlement[]
  loading: boolean
  mutating: boolean
  message: string
  mutationMessage: string
  lastDeletedSettlementId?: string
}

const initialState: ExecutiveCommissionsState = {
  executives: [],
  settlements: [],
  loading: false,
  mutating: false,
  message: '',
  mutationMessage: ''
}

const slice = createSlice({
  name: 'executiveCommissions',
  initialState,
  reducers: {
    loadExecutives: state => state,
    executivesLoaded: (state, action: PayloadAction<ExecutiveOption[]>) => {
      state.executives = action.payload
    },
    loadCommissionData: (state, _action: PayloadAction<LoadPayload>) => {
      state.loading = true
      state.message = ''
    },
    commissionDataLoaded: (
      state,
      action: PayloadAction<{
        preview: CommissionPreview
        settlements: CommissionSettlement[]
      }>
    ) => {
      state.loading = false
      state.preview = action.payload.preview
      state.settlements = action.payload.settlements
    },
    commissionDataFailed: (state, action: PayloadAction<string>) => {
      state.loading = false
      state.mutating = false
      state.message = action.payload
    },
    createSettlement: (state, _action: PayloadAction<CreateSettlementPayload>) => {
      state.mutating = true
      state.mutationMessage = ''
    },
    deleteSettlement: (state, _action: PayloadAction<DeleteSettlementPayload>) => {
      state.mutating = true
      state.mutationMessage = ''
      state.lastDeletedSettlementId = undefined
    },
    settlementDeleted: (state, action: PayloadAction<DeleteSettlementResult>) => {
      state.mutating = false
      state.mutationMessage = 'La liquidación fue eliminada y sus conceptos quedaron disponibles nuevamente.'
      state.lastDeletedSettlementId = action.payload.id
      state.settlements = state.settlements.filter(settlement => settlement._id !== action.payload.id)
    },
    settlementCreated: (state, action: PayloadAction<CommissionSettlement>) => {
      state.mutating = false
      state.mutationMessage = 'La comision fue liquidada correctamente.'
      state.settlements.unshift(action.payload)
    },
    clearMutationMessage: state => {
      state.mutationMessage = ''
    },
    clearExecutiveCommissions: () => initialState
  }
})

export const selectExecutiveCommissions = createDraftSafeSelector(
  [(state: GlobalState) => state.executiveCommissions],
  state => state
)

export const {
  loadExecutives,
  executivesLoaded,
  loadCommissionData,
  commissionDataLoaded,
  commissionDataFailed,
  createSettlement,
  deleteSettlement,
  settlementDeleted,
  settlementCreated,
  clearMutationMessage,
  clearExecutiveCommissions
} = slice.actions

export default slice.reducer
